// ✅ KycDetailsCard.jsx (CACHE-FIRST + BACKGROUND REFRESH)
// Path: src/models/KycDetailsCard.jsx

import React, { useEffect, useMemo, useState } from "react";
import VisibilityIcon from "@mui/icons-material/Visibility";

/* =======================
   ✅ Simple cache helpers
   (kept inside file so you can just paste)
======================= */
const cacheGet = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;

    const { value, expiresAt } = parsed;
    if (expiresAt && Date.now() > expiresAt) {
      localStorage.removeItem(key);
      return null;
    }
    return value ?? null;
  } catch {
    return null;
  }
};

const cacheSet = (key, value, ttlMs = 1000 * 60 * 5) => {
  try {
    const payload = { value, expiresAt: ttlMs ? Date.now() + ttlMs : null };
    localStorage.setItem(key, JSON.stringify(payload));
  } catch {}
};

const normalizePhone = (v) => String(v ?? "").replace(/\D/g, "").slice(-10);

export default function KycDetailsCard({ phone, apiUrl }) {
  const safePhone = useMemo(() => normalizePhone(phone), [phone]);
  const CACHE_KEY = useMemo(
    () => (safePhone ? `KYC_DETAILS_CACHE_${safePhone}` : ""),
    [safePhone]
  );

  const [kycList, setKycList] = useState(() => {
    // ✅ show cached data instantly if exists
    const cached = CACHE_KEY ? cacheGet(CACHE_KEY) : null;
    return Array.isArray(cached) ? cached : [];
  });

  const [currentPage, setCurrentPage] = useState(0);

  // ✅ loading only if nothing cached
  const [loading, setLoading] = useState(() => {
    const cached = CACHE_KEY ? cacheGet(CACHE_KEY) : null;
    return !Array.isArray(cached);
  });

  const [refreshing, setRefreshing] = useState(false);

  // ✅ normalize any object keys: trims spaces like " VISITING_CARD"
  const normalizeRecord = (obj) => {
    if (!obj || typeof obj !== "object") return obj;
    const out = {};
    Object.keys(obj).forEach((k) => {
      const nk = String(k).trim();
      out[nk] = obj[k];
    });
    return out;
  };

  // ✅ read field by trying multiple key variants
  const pick = (obj, keys = []) => {
    if (!obj) return "";
    for (const k of keys) {
      const key = String(k).trim();
      const v = obj?.[key];
      if (v !== undefined && v !== null && String(v).trim() !== "") return v;
    }
    return "";
  };

  // ✅ parse "20/01/2026 03:52:19"
  const parseIndianDateTime = (s) => {
    if (!s) return 0;
    const str = String(s).trim();
    const m = str.match(
      /^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2}):(\d{2})$/
    );
    if (!m) return 0;
    const [, dd, mm, yyyy, HH, MI, SS] = m;
    return Date.UTC(
      Number(yyyy),
      Number(mm) - 1,
      Number(dd),
      Number(HH),
      Number(MI),
      Number(SS)
    );
  };

  const openLink = (url) => {
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const toLinks = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val.filter(Boolean).map(String);

    const s = String(val).trim();
    if (!s) return [];

    return s
      .split(/,|\n/g)
      .map((x) => x.trim())
      .filter(Boolean);
  };

  // ✅ main fetch (cache-first + background refresh)
  useEffect(() => {
    const fetchKyc = async () => {
      if (!safePhone || !apiUrl) {
        setLoading(false);
        setRefreshing(false);
        setKycList([]);
        return;
      }

      const controller = new AbortController();
      let ignore = false;

      // ✅ If cached exists, show it instantly & do background refresh
      const cached = CACHE_KEY ? cacheGet(CACHE_KEY) : null;
      if (Array.isArray(cached) && cached.length) {
        setKycList(cached);
        setLoading(false);
      } else {
        setLoading(true);
      }

      try {
        setRefreshing(true);

        const res = await fetch(`${apiUrl}?phone=${encodeURIComponent(safePhone)}`, {
          cache: "no-store",
          signal: controller.signal,
        });

        const json = await res.json();

        if (ignore) return;

        if (!Array.isArray(json)) {
          // keep cache if already present
          if (!cached) setKycList([]);
          return;
        }

        const normalized = json.map(normalizeRecord);

        // ✅ Only keep records where KYC_MOBILE_NUMBER matches logged-in phone
        const matched = normalized.filter((item) => {
          const submittedMobile = String(pick(item, ["KYC_MOBILE_NUMBER"])).trim();
          const loggedMobile = String(safePhone).trim();
          return submittedMobile === loggedMobile;
        });

        // ✅ newest first (by TIME STAMP)
        matched.sort((a, b) => {
          const ta = parseIndianDateTime(
            pick(a, ["TIME STAMP", "TIME_STAMP", "TIMESTAMP"])
          );
          const tb = parseIndianDateTime(
            pick(b, ["TIME STAMP", "TIME_STAMP", "TIMESTAMP"])
          );
          return tb - ta;
        });

        setKycList(matched);
        setCurrentPage(0);

        // ✅ cache for fast refresh
        if (CACHE_KEY) cacheSet(CACHE_KEY, matched, 1000 * 60 * 5); // 5 min
      } catch (err) {
        // silent: keep cached data if any
        console.error("KYC fetch error:", err);
      } finally {
        if (!ignore) {
          setLoading(false);
          setRefreshing(false);
        }
      }

      return () => {
        ignore = true;
        controller.abort();
      };
    };

    fetchKyc();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safePhone, apiUrl, CACHE_KEY]);

  const data = useMemo(() => kycList[currentPage] || null, [kycList, currentPage]);

  // ✅ status
  const rawStatus = String(pick(data, ["STATUS"])).trim().toUpperCase();
  const kycStatus = rawStatus || "PENDING";

  const statusStyles = {
    APPROVED: "bg-green-100 text-green-800 border border-green-200",
    REJECTED: "bg-red-100 text-red-800 border border-red-200",
    PENDING: "bg-yellow-100 text-yellow-800 border border-yellow-200",
  };
  const statusClass =
    statusStyles[kycStatus] || "bg-gray-100 text-gray-700 border border-gray-200";

  const ledgerLinks = toLinks(
    pick(data, ["PAST_YEAR_LEDGERS", "PASTYEAR_LEDGERS", "LEDGERS", "LEDGER"])
  );
  const godownPhotoLinks = toLinks(pick(data, ["GODOWN_PHOTOS", "GODOWN_PHOTO"]));

  if (loading && !kycList.length) {
    return (
      <div className="bg-white rounded-3xl shadow-2xl p-6">
        <p className="font-bold text-gray-600">Loading KYC details...</p>
      </div>
    );
  }

  if (!kycList.length) {
    return (
      <div className="bg-white rounded-3xl shadow-2xl p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-extrabold text-gray-800">KYC Details</h2>
            <p className="text-sm text-gray-500">Dealer verification documents</p>
          </div>
          {refreshing ? (
            <span className="text-xs font-bold text-gray-500">Refreshing…</span>
          ) : null}
        </div>

        <p className="font-bold text-red-600 py-8 text-center">
          No KYC records found for your registered mobile number
        </p>
        <p className="text-sm text-gray-500 text-center">
          Please submit KYC with this phone number ({safePhone}) to proceed.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-extrabold text-gray-800">KYC Details</h2>
          <p className="text-sm text-gray-500 mt-1">Dealer verification documents</p>
          <p className="text-xs text-gray-400 mt-1">
            Submitted:{" "}
            {pick(data, ["TIME STAMP", "TIME_STAMP", "TIMESTAMP"]) || "—"}
          </p>
          {refreshing ? (
            <p className="text-[11px] font-bold text-gray-500 mt-1">
              Refreshing latest record…
            </p>
          ) : null}
        </div>

        <span className={`px-5 py-2 rounded-2xl font-extrabold text-sm ${statusClass}`}>
          {kycStatus}
        </span>
      </div>

      {/* Dealer Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-5 rounded-2xl border">
        <Info label="Dealer Name" value={pick(data, ["DEALER_NAME"])} />
        <Info label="Firm Name" value={pick(data, ["FIRM"])} />
        <Info label="Phone (GST)" value={pick(data, ["PHONE_NUMBER"])} />
        <Info label="Email" value={pick(data, ["EMAIL"])} />
        <Info label="GST Number" value={pick(data, ["GST_NUMBER"])} />
        <Info label="KYC Mobile Number" value={pick(data, ["KYC_MOBILE_NUMBER"])} />
      </div>

      {/* Godown / Extra fields */}
      <div className="bg-white rounded-3xl shadow-xl p-5 border space-y-3">
        <h3 className="text-lg font-extrabold text-gray-800">Godown Details</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Info label="Godown Type" value={pick(data, ["GODOWN_TYPE"])} />
        </div>

        {/* Godown Photos */}
        <div className="pt-2">
          <h4 className="font-extrabold text-gray-800">Godown Photos</h4>
          {godownPhotoLinks.length ? (
            <div className="mt-2 flex flex-wrap gap-2">
              {godownPhotoLinks.map((u, idx) => (
                <button
                  key={idx}
                  onClick={() => openLink(u)}
                  className="inline-flex items-center gap-1 px-4 py-2 rounded-xl bg-yellow-500 text-white font-bold hover:bg-yellow-600 transition"
                >
                  <VisibilityIcon sx={{ fontSize: 18 }} />
                  View Photo {idx + 1}
                </button>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 mt-1">No godown photos uploaded.</p>
          )}
        </div>
      </div>

      {/* References */}
      <div className="bg-white rounded-3xl shadow-xl p-5 border space-y-3">
        <h3 className="text-lg font-extrabold text-gray-800">Reference 1</h3>
        <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">
          {pick(data, ["REFERENCE_1", "REF_1"]) || "—"}
        </p>
      </div>

      <div className="bg-white rounded-3xl shadow-xl p-5 border space-y-3">
        <h3 className="text-lg font-extrabold text-gray-800">Reference 2</h3>
        <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">
          {pick(data, ["REFERENCE_2", "REF_2"]) || "—"}
        </p>
      </div>

      {/* Documents */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm border rounded-2xl overflow-hidden">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-4 text-left">Document</th>
              <th className="p-4 text-center">Status</th>
              <th className="p-4 text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            <DocRow title="PAN Card" link={pick(data, ["PAN_CARD"])} onView={openLink} />
            <DocRow title="Aadhaar Card" link={pick(data, ["AADHAR_CARD"])} onView={openLink} />
            <DocRow
              title="Electricity Bill"
              link={pick(data, ["GODOWN_ELECTRICITY_BILL"])}
              onView={openLink}
            />
            <DocRow
              title="Cancel Cheque"
              link={pick(data, ["CANCEL_CHEQUE"])}
              onView={openLink}
            />
            <DocRow
              title="Visiting Card"
              link={pick(data, ["VISITING_CARD", "VISITING_CARD_PHOTO"])}
              onView={openLink}
            />
          </tbody>
        </table>
      </div>

      {/* Past Year Ledgers */}
      <div className="bg-white rounded-3xl shadow-xl p-5 border space-y-3">
        <h3 className="text-lg font-extrabold text-gray-800">Past Year Ledgers</h3>

        {ledgerLinks.length ? (
          <div className="flex flex-wrap gap-2">
            {ledgerLinks.map((u, idx) => (
              <button
                key={idx}
                onClick={() => openLink(u)}
                className="inline-flex items-center gap-1 px-4 py-2 rounded-xl bg-yellow-500 text-white font-bold hover:bg-yellow-600 transition"
              >
                <VisibilityIcon sx={{ fontSize: 18 }} />
                View Ledger {idx + 1}
              </button>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No ledgers uploaded.</p>
        )}
      </div>

      {/* Pagination */}
      {kycList.length > 1 && (
        <div className="flex justify-between items-center pt-4">
          <button
            disabled={currentPage === 0}
            onClick={() => setCurrentPage((p) => p - 1)}
            className="px-4 py-2 rounded-xl bg-gray-200 font-bold disabled:opacity-50 hover:bg-gray-300 transition"
          >
            Previous
          </button>

          <span className="text-sm font-bold">
            {currentPage + 1} of {kycList.length}
          </span>

          <button
            disabled={currentPage === kycList.length - 1}
            onClick={() => setCurrentPage((p) => p + 1)}
            className="px-4 py-2 rounded-xl bg-gray-200 font-bold disabled:opacity-50 hover:bg-gray-300 transition"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

/* ---------------- HELPERS ---------------- */

const Info = ({ label, value }) => (
  <div>
    <p className="text-[11px] font-bold text-gray-500 uppercase tracking-widest">
      {label}
    </p>
    <p className="text-sm font-extrabold text-gray-800 mt-1 break-words">
      {value || "—"}
    </p>
  </div>
);

const DocRow = ({ title, link, onView }) => (
  <tr className="border-t hover:bg-gray-50 transition">
    <td className="p-4 font-bold">{title}</td>
    <td className="p-4 text-center">
      {link ? (
        <span className="px-3 py-1 rounded-full text-xs font-extrabold bg-green-100 text-green-700">
          Uploaded
        </span>
      ) : (
        <span className="px-3 py-1 rounded-full text-xs font-extrabold bg-gray-200 text-gray-600">
          Not Uploaded
        </span>
      )}
    </td>
    <td className="p-4 text-center">
      {link ? (
        <button
          onClick={() => onView(link)}
          className="inline-flex items-center gap-1 px-4 py-2 rounded-xl bg-yellow-500 text-white font-bold hover:bg-yellow-600 transition"
        >
          <VisibilityIcon sx={{ fontSize: 18 }} />
          View
        </button>
      ) : (
        <span className="text-gray-400">—</span>
      )}
    </td>
  </tr>
);
